import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, FileText, Download, AlertCircle, Maximize2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * EscrituraPDFViewer
 * --------------------------------------------------------------------
 * Visor del PDF original de la escritura hipotecaria analizada por IA.
 * Carga el PDF como blob con auth header y lo muestra en <iframe>.
 * Si no hay análisis aún, muestra placeholder amable.
 *
 * Props:
 *   - reclamacionId: int
 */
export default function EscrituraPDFViewer({ reclamacionId }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filename, setFilename] = useState('escritura.pdf');

  useEffect(() => {
    if (!reclamacionId) return;
    let cancelled = false;
    let currentUrl = null;
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('lexduo_token');
    fetch(`${API}/api/extrajudicial/reclamaciones/${reclamacionId}/escritura-pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404) {
          setError('Aún no hay escritura subida en este expediente. Sube una en el card "Análisis IA" más arriba.');
          setLoading(false);
          return;
        }
        if (!res.ok) {
          const txt = await res.text().catch(() => '');
          throw new Error(txt || `HTTP ${res.status}`);
        }
        // Capturar filename del header Content-Disposition
        const cd = res.headers.get('Content-Disposition') || '';
        const m = /filename="?([^";]+)"?/i.exec(cd);
        if (m) setFilename(m[1]);
        const blob = await res.blob();
        currentUrl = URL.createObjectURL(blob);
        setBlobUrl(currentUrl);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || 'Error cargando el PDF');
        setLoading(false);
      });

    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [reclamacionId]);

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleFullscreen = () => {
    if (!blobUrl) return;
    window.open(blobUrl, '_blank');
  };

  return (
    <Card className="w-full" data-testid="escritura-pdf-viewer">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Escritura hipotecaria — Visor PDF
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Visor del PDF original subido para el análisis IA. Usa los controles del navegador para zoom y navegación.
          </p>
        </div>
        {blobUrl && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handleFullscreen} data-testid="pdf-fullscreen-btn">
              <Maximize2 className="w-4 h-4 mr-1" /> Pantalla completa
            </Button>
            <Button size="sm" variant="outline" onClick={handleDownload} data-testid="pdf-download-btn">
              <Download className="w-4 h-4 mr-1" /> Descargar
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center gap-3 justify-center py-16 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            Cargando PDF…
          </div>
        )}
        {!loading && error && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-300 rounded-lg" data-testid="pdf-error">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-900">{error}</p>
          </div>
        )}
        {!loading && blobUrl && (
          <iframe
            src={blobUrl}
            title="Escritura hipotecaria"
            className="w-full rounded-md border border-slate-200"
            style={{ height: '80vh', minHeight: 600 }}
            data-testid="pdf-iframe"
          />
        )}
      </CardContent>
    </Card>
  );
}
